import React from 'react';
import _ from 'underscore';

import {
  ProcessExecutionType,
  ProcessClientOrigin,
  ProcessPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  processExecutionTypeDisplayName,
  getEnabledProcessExecutionTypesForPlatformFeature,
} from 'processes/ProcessConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import {listToReadable} from 'util/NameUtil';

interface ProcessTaskSchemaExecutionTypeDropdownProps {
  onChangeExecutionTypes: (executionTypes: Array<string>) => void;
  selectedExecutionTypes: Array<ProcessExecutionType>;
}

const ALL_EXECUTION_TYPES_TEXT = 'Execution type: All';

export default function ProcessTaskSchemaExecutionTypeDropdown(
  props: ProcessTaskSchemaExecutionTypeDropdownProps
): JSX.Element {
  // This component is only valid for process client origin, so we can just hardcode it
  const clientOrigin = ProcessClientOrigin.PROCESSES;

  const allExecutionTypes = getEnabledProcessExecutionTypesForPlatformFeature(
    ProcessPlatformFeatureType.SCHEMA_CONFIG
  );
  const isAllEnabledOptions = (selectedOptions: Array<ProcessExecutionType>): boolean =>
    selectedOptions.length === allExecutionTypes.length;

  const _handleSelectExecutionType = (
    executionType: ProcessExecutionType | Array<ProcessExecutionType>
  ): void => {
    const executionTypes = Array.isArray(executionType) ? executionType : [executionType];

    if (isAllEnabledOptions(executionTypes)) {
      if (isAllEnabledOptions(props.selectedExecutionTypes)) {
        props.onChangeExecutionTypes([]);
      } else {
        props.onChangeExecutionTypes(allExecutionTypes);
      }
    } else {
      const alreadySelected = executionTypes.every((currType) =>
        props.selectedExecutionTypes.includes(currType)
      );
      if (alreadySelected) {
        props.onChangeExecutionTypes(_.difference(props.selectedExecutionTypes, executionTypes));
      } else {
        props.onChangeExecutionTypes(_.union(props.selectedExecutionTypes, executionTypes));
      }
    }
  };

  const _getExecutionTypesText = (): string => {
    if (props.selectedExecutionTypes.length) {
      if (props.selectedExecutionTypes.length === allExecutionTypes.length) {
        return ALL_EXECUTION_TYPES_TEXT;
      }

      return `Execution type: ${listToReadable(
        // this component is only availble for the processes client origin
        props.selectedExecutionTypes.map((executionType) =>
          processExecutionTypeDisplayName(clientOrigin, executionType)
        )
      )}`;
    }
    return 'Filter by execution type...';
  };

  return (
    <SelectDropdown
      menu={
        <Menu
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.MEDIUM}
          value={props.selectedExecutionTypes}
          isMulti={true}
          isValueEqual={(selected, currVal) => {
            if (currVal === ALL_EXECUTION_TYPES_TEXT) {
              return allExecutionTypes.length === props.selectedExecutionTypes.length;
            }
            return selected === currVal;
          }}
        >
          <Section>
            <Option
              value={ALL_EXECUTION_TYPES_TEXT}
              onSelect={() => {
                _handleSelectExecutionType(allExecutionTypes);
              }}
            >
              {ALL_EXECUTION_TYPES_TEXT}
            </Option>
          </Section>
          <Section>
            {/* eslint-disable-next-line no-restricted-syntax */}
            {allExecutionTypes.sort().map((executionType) => (
              <Option
                key={executionType}
                value={executionType}
                onSelect={() => {
                  _handleSelectExecutionType(executionType);
                }}
              >
                {processExecutionTypeDisplayName(clientOrigin, executionType)}
              </Option>
            ))}
          </Section>
        </Menu>
      }
      displayName={_getExecutionTypesText()}
    />
  );
}
